import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from './ui/accordion';
import { HelpCircle, MessageCircle } from 'lucide-react';
import { mockData } from '../mock';

export const FAQSection = ({ content }) => {
  const navigate = useNavigate();
  // Default data for fallback
  const defaultData = {
    header: {
      badge: 'Got Questions?',
      title: 'Frequently Asked Questions',
      subtitle: 'Everything you need to know about how FixDirectly works, our bidding process, and how we ensure quality service for every project.'
    },
    questions: mockData.faqs.map((faq, index) => ({
      question: faq.question,
      answer: faq.answer,
      order: index + 1
    })),
    footer: {
      title: 'Still Have Questions?',
      subtitle: 'Our customer support team is here to help you understand how our platform works and guide you through your first project.',
      cta_primary: {
        text: 'Contact Support',
        url: '/contact'
      },
      cta_secondary: {
        text: 'Schedule a Demo',
        url: '/demo'
      }
    }
  };

  // Use content from API or fallback to default
  // Ensure we have the complete structure by merging with defaults
  const faqData = {
    header: {
      badge: content?.header?.badge || defaultData.header.badge,
      title: content?.header?.title || defaultData.header.title,
      subtitle: content?.header?.subtitle || defaultData.header.subtitle
    },
    questions: content?.questions || defaultData.questions,
    footer: {
      title: content?.footer?.title || defaultData.footer.title,
      subtitle: content?.footer?.subtitle || defaultData.footer.subtitle,
      cta_primary: {
        text: content?.footer?.cta_primary?.text || defaultData.footer.cta_primary.text,
        url: content?.footer?.cta_primary?.url || defaultData.footer.cta_primary.url
      },
      cta_secondary: {
        text: content?.footer?.cta_secondary?.text || defaultData.footer.cta_secondary.text,
        url: content?.footer?.cta_secondary?.url || defaultData.footer.cta_secondary.url
      }
    }
  };

  return (
    <section id="faq" className="py-20 bg-gray-50">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center space-x-2 bg-emerald-100 text-emerald-700 px-4 py-2 rounded-full text-sm font-medium mb-4">
            <HelpCircle size={16} />
            <span>{faqData.header.badge}</span>
          </div>
          <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
            Frequently Asked{' '}
            <span className="text-emerald-600">Questions</span>
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            {faqData.header.subtitle}
          </p>
        </div>

        {/* FAQ Accordion */}
        <div className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden">
          <Accordion type="single" collapsible className="w-full">
            {faqData.questions.map((faq, index) => (
              <AccordionItem key={index} value={`item-${index}`} className="border-b border-gray-100 last:border-b-0">
                <AccordionTrigger className="px-8 py-6 text-left hover:bg-gray-50 transition-colors duration-200">
                  <span className="text-lg font-semibold text-gray-900 pr-4">
                    {faq.question}
                  </span>
                </AccordionTrigger>
                <AccordionContent className="px-8 pb-6">
                  <p className="text-gray-600 leading-relaxed text-base">
                    {faq.answer}
                  </p>
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>

        {/* Contact Support */}
        <div className="mt-12 text-center">
          <div className="bg-white rounded-xl p-8 shadow-lg border border-gray-100 max-w-2xl mx-auto">
            <div className="flex items-center justify-center mb-4">
              <div className="bg-emerald-100 p-3 rounded-full">
                <MessageCircle className="text-emerald-600" size={24} />
              </div>
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-4">
              {faqData.footer.title}
            </h3>
            <p className="text-gray-600 mb-6">
              {faqData.footer.subtitle}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button
                onClick={() => navigate('/contact')}
                className="bg-emerald-600 hover:bg-emerald-700 text-white px-6 py-3 rounded-xl font-medium transition-all duration-200"
              >
                {faqData.footer.cta_primary.text}
              </button>
              <button
                onClick={() => navigate('/contact')}
                className="border border-emerald-600 text-emerald-600 hover:bg-emerald-50 px-6 py-3 rounded-xl font-medium transition-all duration-200"
              >
                {faqData.footer.cta_secondary.text}
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};